import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatError, MatFormField, MatHint, MatInput, MatLabel } from '@angular/material/input';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, RouterLink } from '@angular/router';
import { Modal } from '@spirit-testing/component-library';
import { ModalEscapeGuardDirective } from '../components/modal-escape-guard/modal-escape-guard.directive';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { CustomerService } from '../services/customer.service';
import { CreateCustomerRequest, CustomerDTO, UpdateCustomerRequest } from '../models/customer.model';
import { PageHeaderComponent } from '../components/page-header/page-header.component';
import { StatGroupComponent, StatItem } from '../components/stat-group/stat-group.component';
import { SearchBarComponent } from '../components/search-bar/search-bar.component';
import { StatusPillComponent, customerStatusKind } from '../components/status-pill/status-pill.component';
import { AvatarComponent } from '../components/avatar/avatar.component';
import { OverviewPaginationComponent } from '../components/overview-pagination/overview-pagination.component';
import { ScrollContainerDirective } from '../shared/scroll-container/scroll-container.directive';

const DEFAULT_PAGE_SIZE = 10;

@Component({
  selector: 'app-client-management',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatHint,
    TranslatePipe,
    Modal,
    ModalEscapeGuardDirective,
    MatRadioGroup,
    MatRadioButton,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatTable,
    MatHeaderCellDef,
    MatCellDef,
    MatNoDataRow,
    MatSortModule,
    MatIcon,
    MatButton,
    MatProgressSpinner,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    RouterLink,
    PageHeaderComponent,
    StatGroupComponent,
    SearchBarComponent,
    StatusPillComponent,
    AvatarComponent,
    OverviewPaginationComponent,
    ScrollContainerDirective,
  ],
  templateUrl: './client-management.component.html',
  styleUrl: './client-management.component.scss',
})
export class ClientManagementComponent implements OnInit {
  private readonly customerService = inject(CustomerService);
  private readonly router = inject(Router);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  displayedColumns: string[] = ['name', 'hostingMode', 'azureTenantId', 'status', 'details', 'manage'];
  loading = signal(false);
  error = signal<string | null>(null);

  readonly customers = signal<CustomerDTO[]>([]);
  readonly searchTerm = signal('');
  readonly hostingFilterValue = signal<string | null>(null);
  readonly statusFilterValue = signal<string | null>(null);
  readonly sortActive = signal<string>('');
  readonly sortDirection = signal<'asc' | 'desc' | ''>('');
  readonly pageIndex = signal(0);
  readonly pageSize = signal(DEFAULT_PAGE_SIZE);

  readonly filteredCustomers = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    const hosting = this.hostingFilterValue();
    const status = this.statusFilterValue();
    return this.customers().filter((c) => {
      const matchesSearch = !term || c.name.toLowerCase().includes(term);
      const matchesHosting = !hosting || c.hostingMode === hosting;
      const matchesStatus = !status || c.status === status;
      return matchesSearch && matchesHosting && matchesStatus;
    });
  });

  readonly sortedCustomers = computed(() => {
    const data = this.filteredCustomers();
    const active = this.sortActive();
    const dir = this.sortDirection();
    if (!active || !dir) return data;
    return [...data].sort((a, b) => {
      const valA = String((a as unknown as Record<string, unknown>)[active] ?? '');
      const valB = String((b as unknown as Record<string, unknown>)[active] ?? '');
      const cmp = valA.localeCompare(valB);
      return dir === 'asc' ? cmp : -cmp;
    });
  });

  readonly pagedCustomers = computed(() => {
    const data = this.sortedCustomers();
    if (data.length === 0) return data;
    const size = this.pageSize();
    const maxIndex = Math.max(0, Math.ceil(data.length / size) - 1);
    const start = Math.min(this.pageIndex(), maxIndex) * size;
    return data.slice(start, start + size);
  });

  readonly stats = computed((): StatItem[] => [
    { label: this.translate.instant('clientManagement.stats.total'), value: this.customers().length },
    {
      label: this.translate.instant('clientManagement.stats.active'),
      value: this.customers().filter((c) => c.status === 'ACTIVE').length,
      color: 'var(--color-status-aktiv-fg)',
    },
    {
      label: this.translate.instant('clientManagement.stats.users'),
      value: this.customers().reduce((sum, c) => sum + c.userCount, 0),
    },
  ]);

  showCreateModal = signal(false);
  createError = signal<string | null>(null);
  createSaving = signal(false);

  showEditModal = signal(false);
  editError = signal<string | null>(null);
  editSaving = signal(false);
  selectedCustomer = signal<CustomerDTO | null>(null);

  showDeleteModal = signal(false);
  deleteSaving = signal(false);
  customerToDelete = signal<CustomerDTO | null>(null);

  readonly statusOptions: CustomerDTO['status'][] = ['ACTIVE', 'ONBOARDING', 'INACTIVE'];

  createForm = this.fb.group({
    name: ['', [Validators.required]],
    azureTenantId: ['', [Validators.required]],
    hostingMode: ['' as 'MANAGED' | 'SELF_HOSTED' | '', [Validators.required]],
  });

  editForm = this.fb.group({
    name: ['', [Validators.required]],
    azureTenantId: [''],
    status: ['' as CustomerDTO['status'], [Validators.required]],
  });

  ngOnInit(): void {
    this.loadCustomers();
  }

  onSortChange(sort: Sort): void {
    this.sortActive.set(sort.active);
    this.sortDirection.set(sort.direction);
    this.pageIndex.set(0);
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.pageIndex.set(0);
  }

  hostingPillClass(mode: string): string {
    return 'hosting-pill--' + mode.toLowerCase().replaceAll('_', '-');
  }

  readonly statusKind = customerStatusKind;

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
  }

  setHostingFilter(mode: string | null): void {
    this.hostingFilterValue.set(mode);
  }

  setStatusFilter(status: string | null): void {
    this.statusFilterValue.set(status);
  }

  onCreateOpen(): void {
    this.router.navigate(['/client-management', 'new']);
  }

  onCreateSave(): void {
    this.createForm.markAllAsTouched();
    if (this.createForm.invalid) return;

    this.createSaving.set(true);
    const { name, azureTenantId, hostingMode } = this.createForm.getRawValue();
    const request: CreateCustomerRequest = {
      name,
      azureTenantId,
      hostingMode: hostingMode as 'MANAGED' | 'SELF_HOSTED',
    };

    this.customerService.createCustomer(request).subscribe({
      next: (customer) => {
        this.customers.update((list) => [...list, customer]);
        this.createSaving.set(false);
        this.showCreateModal.set(false);
        this.snackBar.open(this.translate.instant('clientManagement.createModal.success'), undefined, {
          panelClass: 'snackbar-success',
        });
      },
      error: () => {
        this.createError.set('clientManagement.createModal.error');
        this.createSaving.set(false);
        this.snackBar.open(this.translate.instant('clientManagement.createModal.error'), undefined, {
          duration: 4000,
          panelClass: 'snackbar-error',
        });
      },
    });
  }

  onCreateCancel(): void {
    this.showCreateModal.set(false);
  }

  onEditOpen(customer: CustomerDTO): void {
    this.router.navigate(['/client-management', customer.id, 'bearbeiten']);
  }

  onEditSave(): void {
    this.editForm.markAllAsTouched();
    if (this.editForm.invalid) return;

    const customer = this.selectedCustomer();
    if (!customer) return;

    this.editSaving.set(true);
    const { name, azureTenantId, status } = this.editForm.getRawValue();
    const request: UpdateCustomerRequest = {
      name,
      status,
      ...(azureTenantId ? { azureTenantId } : {}),
    };

    this.customerService.updateCustomer(customer.id, request).subscribe({
      next: (updated) => {
        this.customers.update((list) => list.map((c) => (c.id === updated.id ? updated : c)));
        this.editSaving.set(false);
        this.showEditModal.set(false);
        this.snackBar.open(this.translate.instant('clientManagement.editModal.success'), undefined, {
          panelClass: 'snackbar-success',
        });
      },
      error: () => {
        this.editError.set('clientManagement.editModal.error');
        this.editSaving.set(false);
        this.snackBar.open(this.translate.instant('clientManagement.editModal.error'), undefined, {
          duration: 4000,
          panelClass: 'snackbar-error',
        });
      },
    });
  }

  onEditCancel(): void {
    this.showEditModal.set(false);
  }

  onDeleteOpen(customer: CustomerDTO): void {
    this.customerToDelete.set(customer);
    this.showDeleteModal.set(true);
  }

  onDeleteConfirm(): void {
    const customer = this.customerToDelete();
    if (!customer) return;

    this.deleteSaving.set(true);
    this.customerService.deleteCustomer(customer.id).subscribe({
      next: () => {
        this.customers.update((list) => list.filter((c) => c.id !== customer.id));
        this.deleteSaving.set(false);
        this.showDeleteModal.set(false);
        this.snackBar.open(this.translate.instant('clientManagement.deleteModal.success'), undefined, {
          panelClass: 'snackbar-success',
        });
      },
      error: () => {
        this.deleteSaving.set(false);
        this.snackBar.open(this.translate.instant('clientManagement.deleteModal.error'), undefined, {
          duration: 4000,
          panelClass: 'snackbar-error',
        });
      },
    });
  }

  onDeleteCancel(): void {
    this.showDeleteModal.set(false);
  }

  statusBadgeType(status: string): 'success' | 'info' | 'error' {
    if (status === 'ACTIVE') return 'success';
    if (status === 'ONBOARDING') return 'info';
    return 'error';
  }

  private loadCustomers(): void {
    this.loading.set(true);
    this.error.set(null);
    this.customerService.getCustomers().subscribe({
      next: (customers) => {
        this.customers.set(customers);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('clientManagement.table.error');
        this.loading.set(false);
      },
    });
  }
}
